"use client";

import React from "react";
import InputLabel from "@/components/library/InputLabel";
import { Textarea as ShadcnTextarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export interface TextAreaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string | React.ReactNode;
  tooltipText?: string;
  placeholder?: string;
  "data-testid"?: string;
  className?: string;
  containerClassName?: string;
  labelClassName?: string;
  "aria-describedby"?: string;
  "aria-labelledby"?: string;
  disabled?: boolean;
  hideHighlight?: boolean;
  isRequired?: boolean;
  showError?: boolean;
  helperText?: string;
  maxLength?: number;
}

const TextArea: React.FC<TextAreaProps> = ({
  id,
  value,
  onChange,
  onBlur,
  label,
  tooltipText,
  isRequired,
  placeholder,
  ["data-testid"]: dataTestId,
  ["aria-describedby"]: ariaDescribedBy,
  ["aria-labelledby"]: ariaLabelledBy,
  containerClassName,
  labelClassName,
  className,
  disabled,
  showError,
  helperText,
  maxLength,
}) => {
  const isOverLimit = maxLength !== undefined && value.length > maxLength;

  return (
    <div className={cn(containerClassName, label && "mt-2")}>
      {label && typeof label === "string" && tooltipText && (
        <InputLabel
          data-testid={dataTestId && `${dataTestId}_label`}
          htmlFor={id}
          value={isRequired ? `${label} *` : label}
          tooltipText={tooltipText}
        />
      )}
      {label && (typeof label !== "string" || !tooltipText) && (
        <label
          className={cn("block mb-1 font-medium text-800 text-sm", labelClassName)}
          htmlFor={id}
        >
          {typeof label === "string" ? (
            <>
              {label}
              {isRequired ? " *" : ""}
            </>
          ) : (
            label
          )}
        </label>
      )}
      <ShadcnTextarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={dataTestId}
        maxLength={maxLength}
        className={cn(
          "bg-white dark:bg-gray_comp border-gray-300 dark:border-gray-600 rounded-xl min-h-[100px]",
          (showError || isOverLimit) &&
            "border-red-500 ring-red-500 placeholder:text-red-500 dark:border-red-600 dark:ring-red-600 dark:placeholder:text-red-600",
          className
        )}
      />
      <div className="flex justify-between items-start">
        {helperText ? (
          <p
            className={cn(
              "text-sm",
              showError ? "text-red-500 dark:text-red-600" : "text-gray-500 dark:text-gray-400"
            )}
          >
            {helperText}
          </p>
        ) : (
          <span />
        )}
        {maxLength !== undefined && (
          <p
            className={cn(
              "tabular-nums text-xs shrink-0",
              isOverLimit ? "text-red-500 dark:text-red-600" : "text-gray-400 dark:text-gray-500"
            )}
          >
            {value.length}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
};

export default TextArea;
